import { useId, useState } from "react";
import { ChevronDown, Table2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChartDataTableColumn = Readonly<{
  /** Object key in each row whose value is rendered in this column. */
  key: string;
  /** Column header text. */
  label: string;
  /** Accessible short unit annotation appended to the header for assistive tech. */
  unit?: string;
  /** Right-align numeric columns. */
  numeric?: boolean;
}>;

export type ChartDataTableProps = Readonly<{
  /** Chart name used for the toggle label and table caption, e.g. "Claims Volume Trend". */
  chartTitle: string;
  /** Column definitions rendered in order. */
  columns: readonly ChartDataTableColumn[];
  /** Row values; each row must provide every column key. */
  rows: ReadonlyArray<Record<string, string | number | null | undefined>>;
  /** Data-state and provenance context shown above the table so the non-visual
   * equivalent carries the same governance information as the visual chart. */
  contextNote: string;
  /** Optional summary sentence placed above the table. */
  summary?: string;
  /** Number formatting is already applied by callers; values render as provided. */
  className?: string;
}>;

/**
 * Keyboard-operable "View data table" disclosure that exposes a chart's plotted
 * values as a semantic HTML table. Recharts renders charts as SVG shapes with no
 * programmatic data semantics; this provides the WCAG long-description /
 * equivalent-data alternative for screen-reader and keyboard-only users.
 */
export function ChartDataTable({
  chartTitle,
  columns,
  rows,
  contextNote,
  summary,
  className,
}: ChartDataTableProps) {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const toggleLabel = open ? "Hide data table" : "View data table";

  return (
    <div className={cn("mt-3", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background/60 px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Table2 className="h-3.5 w-3.5" />
        {toggleLabel} — {chartTitle}
        <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          id={regionId}
          className="mt-2 overflow-x-auto rounded-md border border-border bg-background/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          tabIndex={0}
          aria-label={`${chartTitle} data table. Use arrow keys to view additional columns.`}
        >
          <div className="px-3 pt-2.5 text-[11px] text-muted-foreground">
            {summary && <p className="mb-1">{summary}</p>}
            <p>{contextNote}</p>
          </div>
          <table className="mt-2 w-full text-left text-xs">
            <caption className="sr-only">
              {chartTitle} — {summary ?? contextNote}
            </caption>
            <thead className="border-y border-border/60 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={cn(
                      "px-3 py-1.5 font-medium",
                      column.numeric && "text-right tabular-nums",
                    )}
                  >
                    {column.label}
                    {column.unit && <span className="sr-only"> ({column.unit})</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border/40 last:border-0">
                  {columns.map((column, columnIndex) => {
                    const value = row[column.key];
                    const text = value === null || value === undefined ? "—" : String(value);
                    const shared = cn(
                      "px-3 py-1.5",
                      column.numeric && "text-right tabular-nums text-foreground",
                    );
                    return columnIndex === 0 ? (
                      <th key={column.key} scope="row" className={cn(shared, "font-medium")}>
                        {text}
                      </th>
                    ) : (
                      <td
                        key={column.key}
                        className={cn(shared, !column.numeric && "text-muted-foreground")}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
